<script>
import { mapGetters } from "vuex";

/**
 * Outer wrapper for all unified print views. Owns:
 *   - paged-A4 visual frame on screen (`.print-page` per page)
 *   - one-time `<style>@page</style>` injection from the active company profile
 *   - `provide()` of profile / mode / voucherDate so descendant Print* components
 *     don't have to repeat the inject themselves
 *   - watermark surface via `data-watermark` on each page
 *   - autoprint / closeAfter / copies / labels query-string handling
 *
 * Slots:
 *   - default: page body (rendered N times when `copies > 1`)
 *   - actions: extra buttons in the on-screen action bar (e.g. "Email")
 *
 * Props:
 *   - mode:     "screen" | "pdf" | "thermal" — affects layout density
 *   - voucherDate: Date — used by descendants to pick the right T&C version
 *   - watermark / watermarkState: optional override (otherwise read from voucher state)
 *   - title: window title shown when route opens in a new tab
 *   - autoTrigger: false to skip ?autoprint handling (used by /dev/print-gallery)
 */
export default {
  name: "PrintLayout",
  props: {
    mode: { type: String, default: "screen" },
    voucherDate: { type: [Date, String], default: null },
    watermark: { type: String, default: null },
    watermarkState: { type: String, default: null },
    title: { type: String, default: null },
    autoTrigger: { type: Boolean, default: true },
  },
  provide() {
    return {
      printProfile: () => this.profile,
      printMode: () => this.mode,
      printVoucherDate: () => this.resolvedVoucherDate,
    };
  },
  computed: {
    ...mapGetters("printProfile", {
      profile: "profile",
      signatories: "signatories",
      bankAccounts: "bankAccounts",
      defaultBankAccount: "defaultBankAccount",
    }),
    paperSize() { return this.profile?.paperSize || "A4"; },
    orientation() { return this.profile?.orientation || "Portrait"; },
    rootClasses() {
      return [
        "print-root",
        `paper-${this.paperSize}`,
        `orient-${this.orientation}`,
        `mode-${this.mode}`,
      ];
    },
    accentVar() {
      const accent = this.profile?.accentColorHex;
      return accent ? { "--print-accent": accent } : {};
    },
    queryFlags() {
      const q = (this.$route && this.$route.query) || {};
      const copies = Math.min(Math.max(parseInt(q.copies, 10) || 1, 1), 10);
      const labelsRaw = q.labels;
      let labels = [];
      if (labelsRaw) {
        labels = String(labelsRaw).split(",").map((s) => s.trim()).filter(Boolean);
      }
      // Validation: when both supplied, lengths must match.
      let labelError = null;
      if (labelsRaw && labels.length !== copies) {
        labelError = `labels (${labels.length}) does not match copies (${copies})`;
      }
      // Default labels when not supplied.
      if (!labelsRaw && copies > 1) {
        labels = Array.from({ length: copies }, (_, i) => `Copy ${i + 1} of ${copies}`);
      }
      return {
        autoprint: q.autoprint === "1" || q.autoprint === "true",
        closeAfter: q.closeAfter === "1" || q.closeAfter === "true",
        embed: q.embed === "1",
        copies,
        labels,
        labelError,
      };
    },
    pageWatermark() {
      // Explicit prop wins; otherwise no watermark — caller pages can
      // override per-copy if needed.
      return this.watermark;
    },
    resolvedVoucherDate() {
      if (!this.voucherDate) return new Date();
      return this.voucherDate instanceof Date ? this.voucherDate : new Date(this.voucherDate);
    },
    pages() {
      const { copies, labels } = this.queryFlags;
      return Array.from({ length: copies }, (_, i) => ({
        index: i,
        label: labels[i] || null,
      }));
    },
  },
  watch: {
    title: {
      immediate: true,
      handler(v) {
        if (v && typeof document !== "undefined") {
          document.title = v;
        }
      },
    },
  },
  mounted() {
    this.injectPageStyle();
    if (!this.autoTrigger) return;

    // Wait for fonts + images, then handle ?autoprint=1.
    Promise.all([this.fontsReady(), this.imagesSettled()])
      .then(() => {
        // Mark global "print ready" sentinel so the server-side Chromium
        // renderer knows when to call page.PdfAsync().
        if (typeof window !== "undefined") {
          window.__printReady = true;
        }
        if (this.queryFlags.autoprint) {
          this.triggerPrint();
        }
      });

    if (this.queryFlags.closeAfter) {
      this.installCloseAfterPrint();
    }
  },
  methods: {
    injectPageStyle() {
      if (typeof document === "undefined") return;
      const profile = this.profile || {};
      const size = profile.paperSize || "A4";
      const orientation = (profile.orientation || "Portrait").toLowerCase();
      const t = profile.marginTopMm ?? 12;
      const r = profile.marginRightMm ?? 10;
      const b = profile.marginBottomMm ?? 12;
      const l = profile.marginLeftMm ?? 10;

      // One-time injection — Chromium honours @page on first paint and
      // ignores subsequent edits, so we never update this.
      let el = document.getElementById("print-page-style");
      if (!el) {
        el = document.createElement("style");
        el.id = "print-page-style";
        document.head.appendChild(el);
      }
      el.textContent = `@page { size: ${size} ${orientation}; margin: ${t}mm ${r}mm ${b}mm ${l}mm; }`;
    },

    fontsReady() {
      if (typeof document === "undefined" || !document.fonts) return Promise.resolve();
      return document.fonts.ready;
    },

    imagesSettled() {
      if (typeof document === "undefined") return Promise.resolve();
      const imgs = Array.from(this.$el.querySelectorAll("img"));
      const promises = imgs.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        });
      });
      return Promise.allSettled(promises);
    },

    triggerPrint() {
      if (typeof window === "undefined") return;
      window.print();
    },

    installCloseAfterPrint() {
      if (typeof window === "undefined") return;
      let closed = false;
      const close = () => {
        if (closed) return;
        closed = true;
        try { window.close(); } catch (_) { /* blocked by popup policy in some browsers */ }
      };
      window.addEventListener("afterprint", close, { once: true });
      // Safari often doesn't fire afterprint — install a 30s fallback.
      setTimeout(close, 30000);
    },
  },
};
</script>

<template>
  <div :class="rootClasses" :style="accentVar" data-print-root>
    <div v-if="queryFlags.labelError" class="print-label-error d-print-none" role="alert">
      {{ queryFlags.labelError }}
    </div>

    <div
      v-for="page in pages"
      :key="page.index"
      class="print-page"
      :data-watermark="pageWatermark || undefined"
      :data-watermark-state="watermarkState || undefined"
      :data-copy-label="page.label || undefined"
    >
      <slot :page="page" :label="page.label" :index="page.index" />
    </div>
  </div>
</template>

<style>
/* Action bar / banner styling — visible only on screen, never printed. */
.print-label-error {
  background: #FEE2E2;
  color: #991B1B;
  border: 1px solid #FCA5A5;
  padding: 8mm;
  margin: 0 auto 4mm;
  max-width: 200mm;
  border-radius: 2mm;
  font-family: var(--print-font, sans-serif);
  font-size: 10pt;
}
</style>
